/**
 * <s-shirt-menu> — the desktop SHOP FOOTBALL SHIRTS megamenu (shirt type → competition → club).
 *
 * Required child markup:
 *   a[data-shirt-menu-trigger] — the top-level link; on touch the first tap opens the panel.
 *   [data-shirt-menu-toggle]   — button that opens/closes the panel (keyboard and touch).
 *   [data-shirt-menu-panel]    — the panel; shown while the element has `data-open`.
 *   a[data-shirt-type]         — type rows; show the matching ul[data-shirt-leagues].
 *   a[data-shirt-league]       — competition rows ("<type>-<index>"); show the matching [data-shirt-clubs].
 *
 * Mouse: opens on hover; rows switch after `data-intent-delay` ms of hover so a diagonal
 * move towards the next column doesn't flip the selection; closes `data-close-delay` ms
 * after the pointer leaves the trigger and panel, and re-entering cancels the close.
 * Touch/pen: the first tap on the trigger opens the panel, the first tap on a row selects
 * it, a second tap follows its link; tapping outside closes. Keyboard: focusing a row
 * selects it; Escape closes and returns focus to the toggle.
 */
class SShirtMenu extends HTMLElement {
  #ac;
  #closeTimer = 0;
  #intentTimer = 0;
  #pointerType = 'mouse';
  #leagueIndex = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const toggle = this.querySelector('[data-shirt-menu-toggle]');
    const trigger = this.querySelector('[data-shirt-menu-trigger]');
    const closeDelay = Number(this.dataset.closeDelay) || 350;
    const intentDelay = Number(this.dataset.intentDelay) || 100;
    this.#leagueIndex = this.querySelector('[data-shirt-league][data-active]')?.getAttribute('data-league-index') ?? '';

    this.addEventListener('pointerdown', (event) => { this.#pointerType = event.pointerType; }, opts);

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.#closeTimer);
      this.#setOpen(true);
    }, opts);

    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.#intentTimer);
      clearTimeout(this.#closeTimer);
      this.#closeTimer = window.setTimeout(() => this.#setOpen(false), closeDelay);
    }, opts);

    toggle?.addEventListener('click', () => this.#setOpen(!this.hasAttribute('data-open')), opts);

    trigger?.addEventListener('click', (event) => {
      const touch = this.#pointerType === 'touch' || this.#pointerType === 'pen';
      if (touch && !this.hasAttribute('data-open')) {
        event.preventDefault();
        this.#setOpen(true);
      }
    }, opts);

    for (const row of this.querySelectorAll('[data-shirt-type], [data-shirt-league]')) {
      if (!(row instanceof HTMLElement)) continue;
      row.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.#intentTimer);
        this.#intentTimer = window.setTimeout(() => this.#select(row), intentDelay);
      }, opts);
      row.addEventListener('pointerleave', () => clearTimeout(this.#intentTimer), opts);
      row.addEventListener('focus', () => this.#select(row), opts);
      row.addEventListener('click', (event) => {
        const touch = this.#pointerType === 'touch' || this.#pointerType === 'pen';
        if (touch && row.hasAttribute('data-has-children') && !row.hasAttribute('data-active')) {
          event.preventDefault();
          this.#select(row);
        }
      }, opts);
    }

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
      this.#setOpen(false);
      if (toggle instanceof HTMLElement) toggle.focus();
      event.stopPropagation();
    }, opts);

    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
      if (this.matches(':hover')) return;
      this.#setOpen(false);
    }, opts);

    // A tap or click outside the menu closes it.
    document.addEventListener('pointerdown', (event) => {
      if (event.target instanceof Node && !this.contains(event.target)) this.#setOpen(false);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#intentTimer);
  }

  #setOpen(open) {
    if (this.hasAttribute('data-open') === open) return;
    this.toggleAttribute('data-open', open);
    this.querySelector('[data-shirt-menu-toggle]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /** @param {HTMLElement} row */
  #select(row) {
    const type = row.dataset.shirtType;
    if (type) {
      this.#activate('[data-shirt-type]', `[data-shirt-type="${type}"]`);
      this.#activate('[data-shirt-leagues]', `[data-shirt-leagues="${type}"]`);
      const league =
        this.querySelector(`[data-shirt-league="${type}-${this.#leagueIndex}"]`) ??
        this.querySelector(`[data-shirt-leagues="${type}"] [data-shirt-league]`);
      if (league instanceof HTMLElement) this.#select(league);
      return;
    }

    const key = row.dataset.shirtLeague;
    if (!key) return;
    this.#leagueIndex = row.dataset.leagueIndex ?? '';
    this.#activate('[data-shirt-league]', `[data-shirt-league="${key}"]`);
    this.#activate('[data-shirt-clubs]', `[data-shirt-clubs="${key}"]`);
  }

  #activate(groupSelector, activeSelector) {
    for (const element of this.querySelectorAll(groupSelector)) {
      element.toggleAttribute('data-active', element.matches(activeSelector));
    }
  }
}

if (!customElements.get('s-shirt-menu')) customElements.define('s-shirt-menu', SShirtMenu);
